import type { Lang, LocaleContent } from '../types/profile';

export const profileName = '0xLift';

export const tools = [
  'Claude',
  'Codex',
  'Cursor',
  'Hermes',
  'Microsoft/Bing Ads',
  'Voluum',
  'UET',
  'LeadsGate',
];

export const profileContent: Record<Lang, LocaleContent> = {
  en: {
    nav: { about: 'About', projects: 'Projects', strengths: 'Strengths' },
    actions: {
      print: 'Print profile',
      languageGroup: 'Language',
      backToTop: 'Back to top',
    },
    eyebrow: 'Personal profile',
    descriptor: 'AI, automation & digital growth',
    subtitle: 'A bit about what I do',
    intro: [
      'I work on digital products, automation, and online marketing. My role usually starts with figuring out what a system needs to do, then organizing the development work, connecting the pieces, and checking that the result works as intended.',
      'I use AI coding tools such as Claude, Codex, Cursor, and Hermes as part of that process, from breaking an idea into tasks to reviewing the results and troubleshooting issues.',
    ],
    aboutLabel: 'About',
    projectsLabel: 'Selected projects',
    projectsHeading: "Projects I've been working on",
    projects: [
      {
        id: 'aurum',
        title: 'AURUM',
        description:
          'A platform for lead-generation websites and application forms across several brands. My work covers the application journey, connections to service providers, and checks before releasing changes.',
        tags: ['Lead forms', 'Multi-brand', 'Provider integrations', 'Release checks'],
      },
      {
        id: 'site-factory',
        title: 'Site Factory and SEO',
        description:
          "Tools to help an operator choose a keyword, enter a domain, select a template, and work through website creation and publishing. I've focused on making those steps clear and repeatable.",
        tags: ['Site creation', 'Templates', 'Publishing workflow'],
      },
      {
        id: 'us-affiliate',
        title: 'US Affiliate Platform',
        description:
          'Ongoing work to adapt an existing SEO platform for the US market, including keyword discovery, content briefs, and workflows for English-language websites.',
        tags: ['Keyword discovery', 'Content briefs', 'English-language sites'],
        status: 'Ongoing',
      },
      {
        id: 'radiosense',
        title: 'RadioSense',
        description:
          "A system for transcribing US radio streams and finding relevant advertising signals. I've worked on the workflow, dashboards, AI analysis, and practical limits of running the system on local GPU hardware.",
        tags: ['Transcription', 'AI analysis', 'Dashboards', 'Local GPU'],
      },
      {
        id: 'flowx',
        title: 'FlowX — Payment Infrastructure',
        description:
          'A payment infrastructure project for Thai businesses. The product website explains QR and bank-transfer collections, automated statement matching, slip fraud checks, wallet balances, and API-based payouts.',
        tags: ['Payments', 'Reconciliation', 'Fraud prevention', 'API'],
        url: 'https://flowx-fit.vercel.app/',
      },
      {
        id: 'performance',
        title: 'Performance Marketing',
        description:
          "I've also worked on Microsoft/Bing Ads campaigns and used Voluum, UET, and LeadsGate to track the journey from an ad click to a landing page and submitted lead.",
        tags: ['Microsoft/Bing Ads', 'Conversion tracking', 'Lead journey'],
      },
    ],
    strengthsLabel: 'Strengths',
    strengthsHeading: 'What I bring to a project',
    strengths:
      'My main strengths are turning business ideas into practical plans, organizing work across AI tools, connecting systems, and working through technical issues. My work brings together product thinking, development coordination, and online marketing.',
    strengthTags: [
      'Product thinking',
      'AI agent workflows',
      'Systems integration',
      'Troubleshooting',
    ],
    toolsHeading: 'Tools I work with',
    footerNote: 'A personal profile — thanks for taking the time to read it.',
    documentTitle: '0xLift — Personal profile',
  },
  th: {
    nav: { about: 'เกี่ยวกับผม', projects: 'โปรเจกต์', strengths: 'สิ่งที่ถนัด' },
    actions: {
      print: 'พิมพ์โปรไฟล์',
      languageGroup: 'ภาษา',
      backToTop: 'กลับขึ้นด้านบน',
    },
    eyebrow: 'โปรไฟล์ส่วนตัว',
    descriptor: 'AI, Automation และการเติบโตทางดิจิทัล',
    subtitle: 'เกี่ยวกับงานที่ผมทำ',
    intro: [
      'ผมทำงานเกี่ยวกับการพัฒนาระบบดิจิทัล Automation และการตลาดออนไลน์ครับ ส่วนใหญ่ผมดูตั้งแต่ไอเดียว่าระบบควรทำอะไร วางขั้นตอนการใช้งาน แล้วประสานการพัฒนา เชื่อมระบบ และตรวจว่าสิ่งที่ได้ทำงานตามโจทย์',
      'ผมใช้ AI เช่น Claude, Codex, Cursor และ Hermes มาช่วยในกระบวนการพัฒนา ตั้งแต่แบ่งงานเป็นขั้นตอน ไปจนถึงตรวจผลและแก้ปัญหาที่เจอ',
    ],
    aboutLabel: 'เกี่ยวกับผม',
    projectsLabel: 'โปรเจกต์',
    projectsHeading: 'โปรเจกต์ที่เคยทำและกำลังทำ',
    projects: [
      {
        id: 'aurum',
        title: 'AURUM',
        description:
          'ระบบเว็บไซต์และฟอร์มรับข้อมูลผู้สนใจสำหรับหลายแบรนด์ ผมดูเรื่องขั้นตอนสมัคร การเชื่อมต่อผู้ให้บริการ และการตรวจงานก่อนนำขึ้นใช้งาน',
        tags: ['ฟอร์มรับข้อมูล', 'หลายแบรนด์', 'เชื่อมต่อผู้ให้บริการ', 'ตรวจก่อนใช้งาน'],
      },
      {
        id: 'site-factory',
        title: 'Site Factory และระบบ SEO',
        description:
          'เครื่องมือช่วยสร้างเว็บไซต์จากคีย์เวิร์ด โดเมน และเทมเพลต ผมเน้นวางขั้นตอนให้คนใช้งานทำตามได้ง่าย ตั้งแต่เตรียมเว็บไซต์จนถึงเผยแพร่',
        tags: ['สร้างเว็บไซต์', 'เทมเพลต', 'ขั้นตอนเผยแพร่'],
      },
      {
        id: 'us-affiliate',
        title: 'US Affiliate Platform',
        description:
          'งานต่อยอดระบบ SEO สำหรับตลาดสหรัฐฯ ครอบคลุมการหาคีย์เวิร์ด เตรียมแนวทางเขียนเนื้อหา และจัดกระบวนการสำหรับเว็บไซต์ภาษาอังกฤษ',
        tags: ['หาคีย์เวิร์ด', 'แนวทางเนื้อหา', 'เว็บไซต์ภาษาอังกฤษ'],
        status: 'กำลังทำ',
      },
      {
        id: 'radiosense',
        title: 'RadioSense',
        description:
          'ระบบถอดเสียงวิทยุสหรัฐฯ และค้นหาสัญญาณโฆษณาที่สนใจ ผมเกี่ยวข้องกับขั้นตอนประมวลผล Dashboard การวิเคราะห์ด้วย AI และการดูข้อจำกัดของเครื่อง GPU ที่ใช้รันระบบ',
        tags: ['ถอดเสียง', 'วิเคราะห์ด้วย AI', 'Dashboard', 'เครื่อง GPU'],
      },
      {
        id: 'flowx',
        title: 'FlowX — Payment Infrastructure',
        description:
          'โปรเจกต์โครงสร้างพื้นฐานระบบรับ–จ่ายเงินสำหรับธุรกิจไทย หน้าเว็บไซต์อธิบายการรับชำระผ่าน QR และโอนเงิน การจับคู่สเตทเมนต์ การตรวจสลิปและป้องกันทุจริต การจัดการยอดวอลเล็ต และการจ่ายเงินผ่าน API',
        tags: ['Payment Gateway', 'กระทบยอด', 'ป้องกันทุจริต', 'API'],
        url: 'https://flowx-fit.vercel.app/',
      },
      {
        id: 'performance',
        title: 'Performance Marketing',
        description:
          'เคยทำแคมเปญ Microsoft/Bing Ads และใช้ Voluum, UET และ LeadsGate ติดตามเส้นทางจากคนคลิกโฆษณา เข้าหน้าเว็บไซต์ จนถึงส่งข้อมูลผ่านฟอร์ม',
        tags: ['Microsoft/Bing Ads', 'ติดตามผล', 'เส้นทางผู้สนใจ'],
      },
    ],
    strengthsLabel: 'สิ่งที่ถนัด',
    strengthsHeading: 'สิ่งที่ผมถนัด',
    strengths:
      'ผมถนัดแปลงไอเดียธุรกิจให้เป็นแผนงานที่ลงมือทำได้ วางขั้นตอนให้เครื่องมือต่าง ๆ ทำงานร่วมกัน แบ่งงานให้ AI Agents และไล่แก้ปัญหาจากผลทดสอบกับการใช้งาน งานของผมจึงมีทั้งฝั่งคิดผลิตภัณฑ์ ประสานงานพัฒนา และการตลาดออนไลน์',
    strengthTags: [
      'การคิดผลิตภัณฑ์',
      'เวิร์กโฟลว์ AI Agents',
      'การเชื่อมต่อระบบ',
      'การไล่แก้ปัญหา',
    ],
    toolsHeading: 'เครื่องมือที่ใช้ทำงาน',
    footerNote: 'โปรไฟล์ส่วนตัว — ขอบคุณที่สละเวลาอ่านครับ',
    documentTitle: '0xLift — โปรไฟล์ส่วนตัว',
  },
};
